import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Tabs,
  Tab,
  TextField,
  Link as MuiLink,
  Typography,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { getOryFrontend } from '../../ory/client';
import { resetAuthSession } from '../../utils/authReset';
import { Styles } from '@t2c/shared-fe';
import { Visibility, VisibilityOff } from '@mui/icons-material';

type TabKey = 'signin' | 'signup';

interface Props {
  onAuthSuccess?: () => void; // call to start Hydra PKCE after password login
  isLoginButtonDisabled: boolean;
}

const EMAIL_REGEX = /.+@.+\..+/;

export const PasswordAuth: React.FC<Props> = ({ onAuthSuccess, isLoginButtonDisabled }) => {
  const ory = useMemo(() => getOryFrontend(), []);

  const [tab, setTab] = useState<TabKey>('signin');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [troubleshootOpen, setTroubleshootOpen] = useState(false);
  const [troubleshootBusy, setTroubleshootBusy] = useState(false);
  const [showSuccessText, setShowSuccessText] = useState(false);

  useEffect(() => {
    if (error) {
      setError(null);
    }
  }, [password, tab, email, name]);

  const startLogin = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const doPasswordLogin = async () => {
        const { data } = await ory.createBrowserLoginFlow();
        const flowId = (data as any).id as string;
        const csrf = (data as any)?.ui?.nodes?.find(
          (n: any) => n?.attributes?.name === 'csrf_token'
        )?.attributes?.value;
        await ory.updateLoginFlow({
          flow: flowId,
          updateLoginFlowBody: {
            method: 'password',
            identifier: email,
            password,
            csrf_token: csrf,
          } as any,
        });
        onAuthSuccess?.();
      };

      // If a Kratos session already exists, check if it belongs to the same email.
      try {
        const sess = await ory.toSession();
        const sessEmail = ((sess as any)?.identity?.traits?.email || '').toLowerCase();
        const inputEmail = (email || '').trim().toLowerCase();

        if (sessEmail && inputEmail && sessEmail === inputEmail) {
          // Same user -> reuse session and proceed to Hydra PKCE
          onAuthSuccess?.();
          return;
        }

        // Different user -> clear Hydra+Kratos in a popup, then perform password login
        const rt = `${window.location.origin}/auth/login-complete`;
        const logoutUrl = `/api/auth/combined-logout?return_to=${encodeURIComponent(rt)}`;

        let resolved = false;
        const waitForPopup = () =>
          new Promise<void>((resolve) => {
            const handler = (ev: MessageEvent) => {
              if (ev.origin !== window.location.origin) return;
              if (ev.data?.type === 'AUTH_LOGIN_DONE' || ev.data?.type === 'AUTH_LOGIN_ERROR') {
                window.removeEventListener('message', handler);
                if (!resolved) {
                  resolved = true;
                  resolve();
                }
              }
            };
            window.addEventListener('message', handler);
            // Safety timeout in case popup can't post back
            setTimeout(() => {
              try {
                window.removeEventListener('message', handler);
              } catch {}
              if (!resolved) {
                resolved = true;
                resolve();
              }
            }, 20000);
          });

        const popup = window.open(
          logoutUrl,
          'logout_window',
          'width=460,height=640,menubar=no,toolbar=no,location=no,status=no'
        );
        if (popup) {
          await waitForPopup();
          await doPasswordLogin();
          return;
        }

        // Popup blocked: fall back to full redirect; user may need to retry once after returning
        window.location.href = logoutUrl;
        return;
      } catch {
        // No existing session -> perform normal password login
      }

      await doPasswordLogin();
    } catch (e: any) {
      const nodes = e?.response?.data?.ui?.nodes || [];
      let msg = null;
      for (const node of nodes) {
        if (Array.isArray(node?.messages) && node.messages.length > 0) {
          msg = node.messages[0]?.text;
          break;
        }
      }
      msg =
        msg ||
        e?.response?.data?.ui?.messages?.[0]?.text ||
        e?.response?.data?.error?.message ||
        e?.message ||
        'Login failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [email, password, ory, onAuthSuccess]);

  const startRegistration = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Prevent duplicate registration if a session already exists
      try {
        await ory.toSession();
        setError('You are already logged in. Please log out to register a different account.');
        return;
      } catch {}

      const { data } = await ory.createBrowserRegistrationFlow();
      const flowId = (data as any).id as string;
      const csrf = (data as any)?.ui?.nodes?.find((n: any) => n?.attributes?.name === 'csrf_token')
        ?.attributes?.value;
      const upd = await ory.updateRegistrationFlow({
        flow: flowId,
        updateRegistrationFlowBody: {
          method: 'password',
          password,
          traits: { email, name },
          csrf_token: csrf,
        } as any,
      });
      // Persist email locally to render in success modal after redirect
      try {
        sessionStorage.setItem('last_registered_email', email);
        // Mark origin so other flows (e.g., Google popup) can reset session if needed
        sessionStorage.setItem('last_flow_origin', 'password-signup');
      } catch {}
      // Clear form inputs after signup submit
      try {
        setEmail('');
        setName('');
        setPassword('');
      } catch {}
      const redirect = (upd as any)?.data?.redirect_browser_to || (upd as any)?.data?.redirect_to;
      if (redirect) {
        window.location.href = redirect as string;
        return;
      }
      onAuthSuccess?.();
    } catch (e: any) {
      const nodes = e?.response?.data?.ui?.nodes || [];
      let msg = null;
      for (const node of nodes) {
        if (Array.isArray(node?.messages) && node.messages.length > 0) {
          msg = node.messages[0]?.text;
          break;
        }
      }
      msg =
        msg ||
        e?.response?.data?.ui?.messages?.[0]?.text ||
        e?.response?.data?.error?.message ||
        e?.message ||
        'Registration failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [email, name, password, ory, onAuthSuccess]);

  const onSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!EMAIL_REGEX.test(email)) {
      setError('Please enter a valid email');
      return;
    }
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (tab === 'signup' && !name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (tab === 'signin') startLogin();
    else startRegistration();
  };

  const startTroubleshootReset = async () => {
    setTroubleshootBusy(true);
    try {
      setShowSuccessText(false);
      await resetAuthSession({ mode: 'popup' });
      setShowSuccessText(true);
    } finally {
      setTroubleshootBusy(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={styles.formContainer}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          textColor="inherit"
          variant="fullWidth"
          sx={{ borderRadius: 1, mb: 2, width: '100%' }}
        >
          <Tab value="signin" label="Sign in" sx={{ width: '50%' }} />
          <Tab value="signup" label="Sign Up" sx={{ width: '50%' }} />
        </Tabs>

        <Box component="form" onSubmit={onSubmit} autoComplete="on" style={{ width: '100%' }}>
          {tab === 'signup' && (
            <TextField
              placeholder="Name"
              variant="outlined"
              fullWidth
              margin="normal"
              value={name}
              sx={styles.textField}
              onChange={(e) => setName(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          )}
          <TextField
            placeholder="Email"
            type="email"
            variant="outlined"
            fullWidth
            margin="normal"
            value={email}
            sx={styles.textField}
            onChange={(e) => setEmail(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            placeholder="Password"
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            fullWidth
            margin="normal"
            value={password}
            sx={styles.textField}
            onChange={(e) => setPassword(e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                    {showPassword ? (
                      <VisibilityOff sx={{ color: 'white' }} />
                    ) : (
                      <Visibility sx={{ color: 'white' }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}

          <LoadingButton
            type="submit"
            fullWidth
            variant="contained"
            loading={loading}
            sx={styles.loginButton}
            disabled={isLoginButtonDisabled}
          >
            {tab === 'signin' ? 'Sign in' : 'Sign Up'}
          </LoadingButton>

          {tab === 'signin' && (
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
              <MuiLink
                href="/auth/recovery"
                underline="hover"
                color="inherit"
                sx={{ opacity: 0.8, fontSize: '12px' }}
              >
                Forgot password?
              </MuiLink>
              <MuiLink
                component="button"
                type="button"
                onClick={() => setTroubleshootOpen(true)}
                underline="hover"
                color="inherit"
                sx={{ opacity: 0.8, fontSize: '12px' }}
              >
                Troubleshoot login
              </MuiLink>
            </Stack>
          )}
        </Box>
      </Box>

      {troubleshootOpen && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2100,
          }}
          onClick={() => !troubleshootBusy && setTroubleshootOpen(false)}
        >
          <Box
            sx={{
              background: '#1a1a1a',
              borderRadius: 2,
              p: 4,
              width: 420,
              textAlign: 'center',
              color: 'white',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Troubleshooting Login issue
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }} gutterBottom>
              {showSuccessText
                ? 'Session has been reset, please try logging in again, if the issue still persits please contact support@txt2create.com.'
                : 'Please wait while we reset your login session. This usually fixes stuck states.'}
            </Typography>
            <Box
              sx={{
                mt: 2,
                mb: 2,
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row',
              }}
            >
              <LoadingButton
                variant="outlined"
                disabled={troubleshootBusy}
                onClick={() => {
                  setTroubleshootOpen(false);
                  setShowSuccessText(false);
                }}
              >
                Close
              </LoadingButton>
              <LoadingButton
                loading={troubleshootBusy}
                loadingIndicator="Resetting…"
                variant="contained"
                onClick={startTroubleshootReset}
                sx={{ color: 'text.primary', boxShadow: 'none' }}
              >
                Reset login session
              </LoadingButton>
            </Box>
          </Box>
        </Box>
      )}

      <Typography variant="body2" color="text.secondary" mt={1.5}>
        By signing in you{' '}
        <span onClick={isLoginButtonDisabled ? onSubmit : () => null}>accept</span> our Terms and
        Conditions
      </Typography>
    </Box>
  );
};

export const styles: Styles = {
  formContainer: {
    justifyContent: 'center',
    backgroundColor: (theme) => theme.palette.background.primaryV2,
    borderRadius: '10px',
    p: '24px',
  },
  textField: {
    backgroundColor: (theme) => theme.palette.background.ternaryV2,
    borderRadius: '6px',
  },
  loginButton: {
    mt: 2,
    height: '56px',
    borderRadius: '6px',
    fontSize: '18px',
    fontWeight: 600,
    color: 'text.primary',
    boxShadow: 'none',
  },
};

export default PasswordAuth;
